import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { RouteRecordNormalized } from 'vue-router'
import { getAuthSystemByUserId, getGovCodeByUserId, getOrganizationCodeByUserId } from '@/utils/auth/user'

export interface RefreshState {
  /** リフレッシュ処理が必表かどうか */
  onRefresh: boolean
  error?: string | null
}
interface Attributes {
  [key: string]: string | Attributes
}
interface CognitoAuth {
  isAuthorized: boolean
  menu: RouteRecordNormalized[]
  attributes: undefined | Attributes
  sensitiveInfo: undefined | object
  refreshState: undefined | RefreshState
}

export const useMiiaFwStore = defineStore('miiaFwStore', () => {
  const loadingView = ref({ isLoading: false, message: '' })
  const loadingImage = ref<null | string>(null)
  const cognitoAuth = ref<CognitoAuth>({
    isAuthorized: false,
    menu: [],
    attributes: undefined,
    sensitiveInfo: undefined,
    refreshState: undefined
  })

  const isLoading = computed(() => loadingView.value.isLoading)
  const loadingMessage = computed(() => loadingView.value.message)
  const isAuthorized = computed(() => cognitoAuth.value.isAuthorized)
  const menu = computed(() => cognitoAuth.value.menu)
  const attributes = computed(() => cognitoAuth.value.attributes)
  const sensitiveInfo = computed(() => cognitoAuth.value.sensitiveInfo)
  const refreshState = computed(() => cognitoAuth.value.refreshState)

  const userId = computed<string | undefined>(
    () =>
      (attributes.value?.userId as string | undefined) ??
      (attributes.value?.user_id as string | undefined)
  )
  const userName = computed(
    () =>
      (attributes.value?.user_name as string | undefined) ??
      ((attributes.value?.additionalInfo as Attributes)?.name as string | undefined)
  )
  const affiliationSystem = computed(() =>
    userId.value ? getAuthSystemByUserId(userId.value) : undefined
  )
  const govCode = computed(() => (userId.value ? getGovCodeByUserId(userId.value) : undefined))
  const organizationCode = computed(() => (userId.value ? getOrganizationCodeByUserId(userId.value) : undefined))

  const enableLoadingView = (argMessage: string) => {
    loadingView.value = { isLoading: true, message: argMessage }
  }
  const disableLoadingView = () => {
    const currentMessage = loadingView.value.message
    loadingView.value = { isLoading: false, message: currentMessage }
  }
  const updateLoadingImage = (imageData: string) => {
    loadingImage.value = imageData
  }
  const updateAuthInfo = (isAuthorized: boolean) => {
    cognitoAuth.value.isAuthorized = isAuthorized
  }
  const updateIsAuthorized = (isAuthorized: boolean) => {
    cognitoAuth.value.isAuthorized = isAuthorized
  }
  const updateMenu = (menu: RouteRecordNormalized[]) => {
    if (Array.isArray(menu)) {
      cognitoAuth.value.menu = menu
    }
  }
  const updateAttributes = (attributes: { [key: string]: string } | undefined) => {
    cognitoAuth.value.attributes = attributes
  }
  const updateSensitiveInfo = (obj: { [key: string]: string } | undefined) => {
    cognitoAuth.value.sensitiveInfo = obj
  }
  const updateRefreshState = (obj: RefreshState) => {
    cognitoAuth.value.refreshState = obj
  }

  const reset = () => {
    loadingView.value = { isLoading: false, message: '' }
    loadingImage.value = null
    cognitoAuth.value = {
      isAuthorized: false,
      menu: [],
      attributes: undefined,
      sensitiveInfo: undefined,
      refreshState: undefined
    }
  }

  return {
    loadingView,
    loadingImage,
    cognitoAuth,
    isLoading,
    loadingMessage,
    isAuthorized,
    menu,
    attributes,
    sensitiveInfo,
    refreshState,
    govCode,
    organizationCode,
    userId,
    userName,
    affiliationSystem,
    updateAuthInfo,
    updateIsAuthorized,
    enableLoadingView,
    disableLoadingView,
    updateLoadingImage,
    updateMenu,
    updateAttributes,
    updateSensitiveInfo,
    updateRefreshState,
    reset
  }
})
